import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import 'react-quill/dist/quill.snow.css';
import {
    Box,
    Pagination,
    IconButton,
    Typography,
    Avatar,
    useMediaQuery,
    useTheme,
    Drawer,
    CircularProgress,
    Alert,
    Snackbar,
    Button,
    TextField,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    Card,
    Chip,
    FormHelperText,
    Tab,
    Tabs,
    List,
    ListItem,
    ListItemText,
    Checkbox,
    ListItemIcon,
    ListItemButton,
    Tooltip,
    FormGroup,
    FormLabel,
    FormControlLabel,
    styled,
    Popover,
    Stack,
    Badge,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Grid,
} from '@mui/material';
import {
    Delete,
    Edit,
    Add,
    Search,
    Refresh,
    PhotoCamera,
    Close,
    Inventory,
    AttachMoney,
    LocalShipping,
    ShoppingBasket,
    FilterAlt,
    Clear,
    Warning,
    CheckCircle,
} from '@mui/icons-material';
import { Clear as ClearIcon } from '@mui/icons-material';
import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';
import ReactQuill from 'react-quill';

import {
    useListBundlesQuery,
    useAddBundleMutation,
    useUpdateBundleMutation,
    useDeleteBundleMutation,
    useGetBundleCountQuery,
    useListProductsQuery,
    Bundle,
    BundleProduct,
    BundleFormData,
    FormErrors,
    SnackbarState,
} from '../../features/admin/productApi.ts';
import { useListCategoriesQuery } from '../../features/admin/categoryApi.ts';
import { useListSubCategoriesQuery } from '../../features/admin/subcategoryApi.ts';
import {
    useListShippingChargesQuery,
    ShippingCharge,
} from '../../features/admin/shippingApi.ts';
import {
    useListDeliveryModelsQuery,
    DeliveryModel,
} from '../../features/admin/deliveryApi.ts';

// ─── Styled Components ─────────────────────────────────────────────────────

const ResponsiveTableContainer = styled(Box)(({ theme }) => ({
    width: '100%',
    overflowX: 'auto',
    '& .MuiTableCell-root': {
        whiteSpace: 'nowrap',
        [theme.breakpoints.down('md')]: {
            padding: '8px 4px',
            fontSize: '12px',
        },
    },
}));

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`bundle-tabpanel-${index}`}
            aria-labelledby={`bundle-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

const shopifyColors = {
    primary: '#008060',
    primaryHover: '#006e52',
    surface: '#f6f6f7',
    border: '#e1e3e5',
    textPrimary: '#202223',
    textSecondary: '#6d7175',
    error: '#d72c0d',
    warning: '#ffc453',
    blue: '#2c6ecb',
};

// ─── Helper: parse delivery_model from API (string | number | number[]) ────
function parseDeliveryModel(raw: any): number[] {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.map(Number).filter((n) => !isNaN(n));
    if (typeof raw === 'number') return [raw];
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) return parsed.map(Number).filter((n) => !isNaN(n));
            const n = Number(parsed);
            return isNaN(n) ? [] : [n];
        } catch {
            const n = Number(raw);
            return isNaN(n) ? [] : [n];
        }
    }
    return [];
}

// ─── Component ──────────────────────────────────────────────────────────────
const BundleManagement: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const { role, vendorId, userName } = useSelector((state: any) => state.auth);
    const isVendor = role === 'vendor';

    // ── pagination / search / filter state ──
    const [page, setPage] = useState(1);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [searchStr, setSearchStr] = useState('');
    const productsPerPage = 10;
    const [statusFilter, setStatusFilter] = useState('all');
    const [availabilityFilter, setAvailabilityFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [subCategoryFilter, setSubCategoryFilter] = useState('all');
    const [sortBy, setSortBy] = useState('newest');
    const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);

    // ── snackbar ──
    const [snackbar, setSnackbar] = useState<SnackbarState>({
        open: false,
        message: '',
        severity: 'success',
    });

    // ── form state ──
    const [formOpen, setFormOpen] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [editingBundle, setEditingBundle] = useState<Bundle | null>(null);
    const [isFormSubmitting, setIsFormSubmitting] = useState(false);
    const [formErrors, setFormErrors] = useState<FormErrors>({});

    const [bundleFormData, setBundleFormData] = useState<BundleFormData>({
        bundle_name: '',
        bundle_short_name: '',
        bundle_desc: '',
        status: 1,
        currency: 'AED',
        bundle_price: 0,
        bundle_retail_price: 0,
        discount_type: 0,
        bundle_discount: 0,
        cat_id: 0,
        cat_name: '',
        sub_cat_id: undefined,
        availability: 1,
        max_order_quantity: 10,
        bundle_stock: 0,
        price_priority: 1,
        delivery_model: [],
        shipping_model: 1,
        tax1_name: 'VAT',
        tax1_per: 0,
        tax2_name: 'GST',
        tax2_per: 0,
        popular_score: 0,
        vendor_id: isVendor ? vendorId : undefined,
        products: [],
    });

    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState('');
    const [availableProducts, setAvailableProducts] = useState<any[]>([]);
    const [selectedProducts, setSelectedProducts] = useState<BundleProduct[]>([]);
    const [productQuantities, setProductQuantities] = useState<{ [key: number]: number }>({});
    const [shippingModels, setShippingModels] = useState<ShippingCharge[]>([]);
    const [availableDeliveryModels, setAvailableDeliveryModels] = useState<DeliveryModel[]>([]);
    const [defaultDeliveryModel, setDefaultDeliveryModel] = useState<DeliveryModel | null>(null);
    const [subCategoryPage, setSubCategoryPage] = useState(1);
    const subCategoriesPerPage = 30;
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [bundleToDelete, setBundleToDelete] = useState<Bundle | null>(null);

    // ── RTK Query hooks ──
    const {
        data: bundlesResponse,
        isLoading: isLoadingBundles,
        isError: isBundlesError,
        refetch: refetchBundles,
        error: bundlesError,
    } = useListBundlesQuery({
        page,
        limit: productsPerPage,
        search_str: searchStr,
        vendor_id: isVendor ? vendorId : undefined,
    });

    const { data: bundleCountResponse, refetch: refetchBundleCount } = useGetBundleCountQuery({
        search_str: searchStr,
        vendor_id: isVendor ? vendorId : undefined,
    });

    const { data: productsResponse, isLoading: isLoadingProducts } = useListProductsQuery({
        page: 1,
        limit: 100,
        vendor_id: isVendor ? vendorId : undefined,
    });

    const { data: categoriesResponse } = useListCategoriesQuery({});

    const { data: subCategoriesResponse, refetch: refetchSubCategories } = useListSubCategoriesQuery(
        { cat_id: bundleFormData.cat_id },
        { skip: !bundleFormData.cat_id }
    );

    const { data: shippingResponse } = useListShippingChargesQuery();
    const { data: deliveryResponse } = useListDeliveryModelsQuery();

    const [addBundle, { isLoading: isAddingBundle }] = useAddBundleMutation();
    const [updateBundle, { isLoading: isUpdatingBundle }] = useUpdateBundleMutation();
    const [deleteBundle, { isLoading: isDeletingBundle }] = useDeleteBundleMutation();

    // ── derived data ──
    const bundles: Bundle[] = useMemo(() => {
        if (!bundlesResponse) return [];
        return bundlesResponse.result || bundlesResponse.data || [];
    }, [bundlesResponse]);

    const categories: any[] = categoriesResponse?.result || categoriesResponse?.data || [];
    const subCategories: any[] = subCategoriesResponse?.result || subCategoriesResponse?.data || [];

    const bundleTotalRecords =
        bundleCountResponse?.count ||
        bundleCountResponse?.result?.count ||
        bundlesResponse?.count ||
        bundles.length ||
        0;
    const bundleTotalPages = Math.ceil(bundleTotalRecords / productsPerPage) || 1;
    const isEditingBundle = Boolean(editingBundle);

    const sortedCategories = useMemo(
        () => [...categories].sort((a, b) => (a.cat_name || '').localeCompare(b.cat_name || '')),
        [categories]
    );

    // FIX: sub-categories filtered by the FORM's cat_id (works for both filter popover and form)
    const filteredSubCategories = useMemo(() => {
        if (!bundleFormData.cat_id) return [];
        return subCategories.filter(
            (sc: any) => Number(sc.cat_id || sc.category_id) === Number(bundleFormData.cat_id)
        );
    }, [subCategories, bundleFormData.cat_id]);

    const totalSubCategoryPages = Math.ceil(filteredSubCategories.length / subCategoriesPerPage) || 1;
    const paginatedSubCategories = filteredSubCategories.slice(
        (subCategoryPage - 1) * subCategoriesPerPage,
        subCategoryPage * subCategoriesPerPage
    );

    const isFilterActive =
        categoryFilter !== 'all' ||
        subCategoryFilter !== 'all' ||
        statusFilter !== 'all' ||
        availabilityFilter !== 'all' ||
        sortBy !== 'newest';

    // sub-categories for the FILTER popover (keyed on categoryFilter, not form cat_id)
    const filteredSubCategoriesForFilter = useMemo(() => {
        if (categoryFilter === 'all') return [];
        const catId = Number(categoryFilter);
        // pull from all categories' sub-cats or from the last fetched set if catId matches form
        return subCategories.filter((sc: any) => Number(sc.cat_id || sc.category_id) === catId);
    }, [categoryFilter, subCategories]);

    const filteredBundles = useMemo(() => {
        if (!Array.isArray(bundles)) return [];
        let list = [...bundles];

        if (searchStr.trim()) {
            const q = searchStr.toLowerCase();
            list = list.filter(
                (b) =>
                    b.bundle_name?.toLowerCase().includes(q) ||
                    b.bundle_short_name?.toLowerCase().includes(q) ||
                    b.bundle_desc?.toLowerCase().includes(q)
            );
        }

        if (categoryFilter !== 'all') list = list.filter((b) => Number(b.cat_id) === Number(categoryFilter));
        if (subCategoryFilter !== 'all')
            list = list.filter((b) => Number(b.sub_cat_id) === Number(subCategoryFilter));
        if (statusFilter !== 'all') list = list.filter((b) => Number(b.status) === Number(statusFilter));
        if (availabilityFilter !== 'all')
            list = list.filter((b) => Number(b.availability) === Number(availabilityFilter));

        list.sort((a, b) => {
            switch (sortBy) {
                case 'oldest':
                    return new Date(a.createddt || 0).getTime() - new Date(b.createddt || 0).getTime();
                case 'name_asc':
                    return (a.bundle_name || '').localeCompare(b.bundle_name || '');
                case 'name_desc':
                    return (b.bundle_name || '').localeCompare(a.bundle_name || '');
                case 'price_asc':
                    return (a.bundle_price || 0) - (b.bundle_price || 0);
                case 'price_desc':
                    return (b.bundle_price || 0) - (a.bundle_price || 0);
                case 'stock_asc':
                    return (a.bundle_stock || 0) - (b.bundle_stock || 0);
                case 'stock_desc':
                    return (b.bundle_stock || 0) - (a.bundle_stock || 0);
                case 'newest':
                default:
                    return new Date(b.createddt || 0).getTime() - new Date(a.createddt || 0).getTime();
            }
        });

        return list;
    }, [bundles, searchStr, categoryFilter, subCategoryFilter, statusFilter, availabilityFilter, sortBy]);

    // ── Effects ──
    useEffect(() => {
        if (shippingResponse?.result) setShippingModels(shippingResponse.result);
    }, [shippingResponse]);

    useEffect(() => {
        if (deliveryResponse?.result) {
            const active = deliveryResponse.result.filter((m: DeliveryModel) => m.status === 1);
            setAvailableDeliveryModels(active);
            const def = deliveryResponse.result.find((m: DeliveryModel) => m.is_default === 1);
            setDefaultDeliveryModel(def || null);
        }
    }, [deliveryResponse]);

    useEffect(() => {
        if (formOpen && productsResponse) {
            const list = productsResponse?.result || productsResponse?.data || [];
            setAvailableProducts(list.filter((p: any) => p.status === 1));
        }
    }, [formOpen, productsResponse]);

    useEffect(() => {
        if (bundleFormData.cat_id > 0) refetchSubCategories();
    }, [bundleFormData.cat_id, refetchSubCategories]);

    useEffect(() => {
        const timer = setTimeout(() => {
            refetchBundles();
            refetchBundleCount();
        }, 400);
        return () => clearTimeout(timer);
    }, [searchStr, refetchBundles, refetchBundleCount]);

    // ── Helpers ──

    // FIX: getCategoryName receives a numeric cat_id and looks it up
    const getCategoryName = (catId: number | string): string => {
        const id = Number(catId);
        const cat = categories.find((c: any) => Number(c.cat_id || c.id) === id);
        return cat?.cat_name || 'N/A';
    };

    const getSubCategoryName = (subCatId: number | string | undefined): string => {
        if (!subCatId) return '';
        const id = Number(subCatId);
        const sc = subCategories.find((s: any) => Number(s.sub_cat_id || s.id) === id);
        return sc?.sub_cat_name || 'N/A';
    };

    const formatCurrencyAED = (amount: number, currency = 'AED') =>
        new Intl.NumberFormat('ar-AE', {
            style: 'currency',
            currency,
            currencyDisplay: 'symbol',
        }).format(amount);

    const quillModules = {
        toolbar: [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline'],
            [{ color: [] }, { background: [] }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ align: [] }],
            ['link'],
            ['clean'],
        ],
    };

    const quillFormats = [
        'header', 'bold', 'italic', 'underline', 'color',
        'background', 'list', 'bullet', 'align', 'link',
    ];

    const renderStatus = (status: number) => {
        const map: Record<number, { label: string; bg: string; color: string }> = {
            1: { label: 'Active', bg: '#d1f4e0', color: '#00783e' },
            0: { label: 'Inactive', bg: '#e4e5e7', color: '#616161' },
            2: { label: 'Suspended', bg: '#ffe8d4', color: '#b24000' },
        };
        const s = map[status] || { label: 'Unknown', bg: '#e4e5e7', color: '#616161' };
        return (
            <Chip
                label={s.label}
                size="small"
                sx={{ bgcolor: s.bg, color: s.color, fontWeight: 600, fontSize: '12px', borderRadius: '6px' }}
            />
        );
    };

    const renderAvailability = (availability: number) => {
        const map: Record<number, { label: string; bg: string; color: string }> = {
            1: { label: 'In Stock', bg: '#d1f4e0', color: '#00783e' },
            0: { label: 'Out of Stock', bg: '#fdeded', color: '#d72c0d' },
            2: { label: 'Pre-order', bg: '#e6f6fc', color: '#006fbb' },
        };
        const a = map[availability] || { label: 'Unknown', bg: '#e4e5e7', color: '#616161' };
        return (
            <Chip
                label={a.label}
                size="small"
                sx={{ bgcolor: a.bg, color: a.color, fontWeight: 600, fontSize: '12px', borderRadius: '6px' }}
            />
        );
    };

    // ── Form helpers ──

    const resetBundleForm = () => {
        setBundleFormData({
            bundle_name: '',
            bundle_short_name: '',
            bundle_desc: '',
            status: 1,
            currency: 'AED',
            bundle_price: 0,
            bundle_retail_price: 0,
            discount_type: 0,
            bundle_discount: 0,
            cat_id: 0,
            cat_name: '',
            sub_cat_id: undefined,
            availability: 1,
            max_order_quantity: 10,
            bundle_stock: 0,
            price_priority: 1,
            delivery_model: [],
            shipping_model: 1,
            tax1_name: 'VAT',
            tax1_per: 0,
            tax2_name: 'GST',
            tax2_per: 0,
            popular_score: 0,
            vendor_id: isVendor ? vendorId : undefined,
            products: [],
        });
        setSelectedProducts([]);
        setProductQuantities({});
        setSelectedImage(null);
        setImagePreview('');
        setFormErrors({});
        setEditingBundle(null);
        setTabValue(0);
    };

    const handleAddBundle = () => {
        resetBundleForm();
        setFormOpen(true);
    };

    const handleEditBundle = (bundle: Bundle) => {
        if (isVendor && bundle.vendor_id !== vendorId) {
            setSnackbar({ open: true, message: 'You can only edit your own bundles', severity: 'error' });
            return;
        }

        // Parse delivery_model robustly
        const dmArr = parseDeliveryModel(bundle.delivery_model);

        // Prepare products
        const productsArray: BundleProduct[] = Array.isArray(bundle.products)
            ? bundle.products.map((p: any) => ({
                product_id: p.product_id,
                product_name: p.product_name || p.prod_name || '',
                product_price: p.product_price || 0,
                product_retail_price: p.product_retail_price || 0,
                product_main_image: p.product_main_image || '',
                quantity: p.quantity || 1,
            }))
            : [];

        const initialQuantities: { [key: number]: number } = {};
        productsArray.forEach((p) => {
            initialQuantities[p.product_id] = p.quantity || 1;
        });

        // FIX: Get category name properly - use bundle.cat_name if available, otherwise look it up
        const resolvedCatName = bundle.cat_name || getCategoryName(bundle.cat_id);

        setBundleFormData({
            bundle_name: bundle.bundle_name || '',
            bundle_short_name: bundle.bundle_short_name || '',  // FIX: Ensure this is set
            bundle_desc: bundle.bundle_desc || '',  // FIX: Ensure this is set
            status: bundle.status ?? 1,
            currency: bundle.currency || 'AED',
            bundle_price: bundle.bundle_price || 0,
            bundle_retail_price: bundle.bundle_retail_price || 0,
            discount_type: bundle.discount_type || 0,
            bundle_discount: bundle.bundle_discount || 0,
            cat_id: bundle.cat_id || 0,
            cat_name: resolvedCatName,  // FIX: Use resolved name
            sub_cat_id: bundle.sub_cat_id,
            availability: bundle.availability ?? 1,
            max_order_quantity: bundle.max_order_quantity || 10,
            bundle_stock: bundle.bundle_stock || 0,
            price_priority: bundle.price_priority || 1,
            delivery_model: dmArr,
            shipping_model: bundle.shipping_model || 1,
            tax1_name: bundle.tax1_name || 'VAT',
            tax1_per: bundle.tax1_per || 0,
            tax2_name: bundle.tax2_name || 'GST',
            tax2_per: bundle.tax2_per || 0,
            popular_score: bundle.popular_score || 0,
            vendor_id: bundle.vendor_id || (isVendor ? vendorId : undefined),
            products: productsArray,
        });

        setSelectedProducts(productsArray);
        setProductQuantities(initialQuantities);

        if (bundle.bundle_main_image) {
            setImagePreview(`https://fftestservice.sarsetech.com/images/${bundle.bundle_main_image}`);
        } else {
            setImagePreview('');
        }

        setEditingBundle(bundle);
        setFormOpen(true);
    };

    const handleFormClose = () => {
        setFormOpen(false);
        setEditingBundle(null);
    };

    const validateForm = (): { isValid: boolean; errorTab: number | null } => {
        const errors: FormErrors = {};
        let errorTab: number | null = null;

        if (!bundleFormData.bundle_name?.trim()) {
            errors.bundle_name = 'Bundle name is required';
            errorTab = errorTab ?? 0;
        }
        if (!bundleFormData.bundle_short_name?.trim()) {
            errors.bundle_short_name = 'Short name is required';
            errorTab = errorTab ?? 0;
        }
        if (bundleFormData.bundle_short_name?.length > 10) {
            errors.bundle_short_name = 'Max 10 characters';
            errorTab = errorTab ?? 0;
        }
        if (!bundleFormData.cat_id) {
            errors.cat_id = 'Category is required';
            errorTab = errorTab ?? 0;
        }
        if (bundleFormData.bundle_price <= 0) {
            errors.bundle_price = 'Price must be > 0';
            errorTab = errorTab ?? 1;
        }
        if (bundleFormData.bundle_retail_price <= 0) {
            errors.bundle_retail_price = 'Retail price must be > 0';
            errorTab = errorTab ?? 1;
        }
        if (selectedProducts.length === 0) {
            errors.products = 'Select at least one product';
            errorTab = errorTab ?? 2;
        }
        if (bundleFormData.bundle_stock < 0) {
            errors.bundle_stock = 'Stock cannot be negative';
            errorTab = errorTab ?? 1;
        }

        setFormErrors(errors);
        return { isValid: Object.keys(errors).length === 0, errorTab };
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const { isValid, errorTab } = validateForm();

        if (!isValid) {
            if (errorTab !== null) setTabValue(errorTab);
            setSnackbar({ open: true, message: 'Please fix the highlighted errors', severity: 'error' });
            return;
        }

        setIsFormSubmitting(true);

        try {
            // Build products array with current quantities
            const productsArray = selectedProducts.map((p) => ({
                product_id: p.product_id,
                quantity: productQuantities[p.product_id] || p.quantity || 1,
            }));

            // Delivery models — fall back to default if none selected
            let deliveryModels = bundleFormData.delivery_model || [];
            if (deliveryModels.length === 0 && defaultDeliveryModel) {
                deliveryModels = [defaultDeliveryModel.id];
            }

            // Resolve cat_name from categories list
            const catName = getCategoryName(bundleFormData.cat_id);

            const formData: any = {
                bundle_name: bundleFormData.bundle_name,
                bundle_short_name: bundleFormData.bundle_short_name,
                bundle_desc: bundleFormData.bundle_desc || '',
                status: bundleFormData.status,
                currency: bundleFormData.currency,
                bundle_price: bundleFormData.bundle_price,
                bundle_retail_price: bundleFormData.bundle_retail_price,
                discount_type: bundleFormData.discount_type,
                bundle_discount: bundleFormData.bundle_discount,
                cat_id: bundleFormData.cat_id,
                cat_name: catName, // FIX: always send resolved cat_name
                sub_cat_id: bundleFormData.sub_cat_id,
                availability: bundleFormData.availability,
                max_order_quantity: bundleFormData.max_order_quantity,
                bundle_stock: bundleFormData.bundle_stock,
                price_priority: bundleFormData.price_priority,
                shipping_model: bundleFormData.shipping_model,
                delivery_model: deliveryModels,
                tax1_name: bundleFormData.tax1_name || 'VAT',
                tax1_per: bundleFormData.tax1_per || 0,
                tax2_name: bundleFormData.tax2_name || 'GST',
                tax2_per: bundleFormData.tax2_per || 0,
                popular_score: bundleFormData.popular_score,
                products: productsArray,
            };

            if (isVendor && vendorId) {
                formData.vendor_id = vendorId;
            } else if (bundleFormData.vendor_id) {
                formData.vendor_id = bundleFormData.vendor_id;
            }

            if (selectedImage) {
                formData.bundle_main_image = selectedImage;
            }

            if (isEditingBundle && editingBundle) {
                const bundleId = editingBundle.bundle_id || editingBundle.id;
                if (bundleId) formData.bundle_id = bundleId;
            }

            let result: any;

            if (isEditingBundle && editingBundle) {
                result = await updateBundle(formData).unwrap();
            } else {
                result = await addBundle(formData).unwrap();
            }

            setSnackbar({
                open: true,
                message: result.message || `Bundle ${isEditingBundle ? 'updated' : 'added'} successfully!`,
                severity: 'success',
            });

            handleFormClose();
            refetchBundles();
            refetchBundleCount();
        } catch (err: any) {
            setSnackbar({
                open: true,
                message: err?.data?.message || err?.message || 'Error saving bundle',
                severity: 'error',
            });
        } finally {
            setIsFormSubmitting(false);
        }
    };

    // ── Delete ──
    const handleDeleteClick = (bundle: Bundle) => {
        setBundleToDelete(bundle);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!bundleToDelete) return;

        const bundleId = bundleToDelete.bundle_id || bundleToDelete.id;
        if (!bundleId) {
            setSnackbar({ open: true, message: 'Invalid bundle ID', severity: 'error' });
            setDeleteDialogOpen(false);
            setBundleToDelete(null);
            return;
        }

        try {
            const result = await deleteBundle({
                bundle_id: bundleId,
                product_id: bundleId,
            }).unwrap();

            setSnackbar({
                open: true,
                message: result.message || 'Bundle deleted successfully!',
                severity: 'success',
            });

            await refetchBundles();
            await refetchBundleCount();
        } catch (err: any) {
            setSnackbar({
                open: true,
                message: err?.data?.message || 'Error deleting bundle',
                severity: 'error',
            });
        } finally {
            setDeleteDialogOpen(false);
            setBundleToDelete(null);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setBundleToDelete(null);
    };

    // ── Input handlers ──
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const numFields = [
            'bundle_price', 'bundle_retail_price', 'bundle_discount', 'bundle_stock',
            'max_order_quantity', 'popular_score', 'tax1_per', 'tax2_per',
        ];

        setBundleFormData((prev) => ({
            ...prev,
            [name]: numFields.includes(name) ? (value === '' ? 0 : Number(value)) : value,
        }));

        if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleSelectChange = (e: SelectChangeEvent<any>) => {
        const { name, value } = e.target;
        setBundleFormData((prev) => ({
            ...prev,
            [name as string]: value === '' ? undefined : value,
        }));
    };

    // FIX: when category changes, also update cat_name and reset sub_cat_id
    const handleCategoryChange = (e: SelectChangeEvent<any>) => {
        const { value } = e.target;
        const catId = Number(value);
        setSubCategoryPage(1);

        // Find the category name
        const cat = categories.find((c: any) => Number(c.cat_id || c.id) === catId);

        setBundleFormData((prev) => ({
            ...prev,
            cat_id: catId,
            cat_name: cat?.cat_name || '',  // FIX: Set cat_name
            sub_cat_id: undefined,
        }));
        if (formErrors.cat_id) setFormErrors((prev) => ({ ...prev, cat_id: '' }));
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!['image/jpeg', 'image/jpg', 'image/png', 'image/gif'].includes(file.type)) {
            setSnackbar({ open: true, message: 'Use JPEG, PNG or GIF', severity: 'error' });
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            setSnackbar({ open: true, message: 'Image must be < 5 MB', severity: 'error' });
            return;
        }

        setSelectedImage(file);
        const reader = new FileReader();
        reader.onload = (ev) => setImagePreview(ev.target?.result as string);
        reader.readAsDataURL(file);
    };

    const handleRemoveImage = () => {
        setSelectedImage(null);
        setImagePreview('');
        const inp = document.getElementById('bundle-image-upload') as HTMLInputElement;
        if (inp) inp.value = '';
    };

    // ── Product selection ──
    const handleProductSelection = (product: any) => {
        const pid = product.product_id || product.id;
        const alreadyIn = selectedProducts.some((p) => p.product_id === pid);

        if (alreadyIn) {
            setSelectedProducts((prev) => prev.filter((p) => p.product_id !== pid));
            setProductQuantities((prev) => {
                const n = { ...prev };
                delete n[pid];
                return n;
            });
        } else {
            setSelectedProducts((prev) => [
                ...prev,
                {
                    product_id: pid,
                    product_name: product.product_name || product.prod_name || '',
                    product_price: product.product_price || 0,
                    product_retail_price: product.product_retail_price || 0,
                    product_main_image: product.product_main_image || '',
                    quantity: 1,
                },
            ]);
            setProductQuantities((prev) => ({ ...prev, [pid]: 1 }));
        }
    };

    const handleProductQuantityChange = (productId: number, qty: number) => {
        const validQty = Math.max(1, qty);
        setProductQuantities((prev) => ({ ...prev, [productId]: validQty }));
        setSelectedProducts((prev) =>
            prev.map((p) => (p.product_id === productId ? { ...p, quantity: validQty } : p))
        );
    };

    // ── Delivery model helpers (unified — no separate temp state needed) ──
    const getCurrentDeliveryModels = (): number[] => bundleFormData.delivery_model || [];

    const setDeliveryModels = (models: number[]) => {
        setBundleFormData((prev) => ({ ...prev, delivery_model: models }));
    };

    // ── Filter helpers ──
    const handleClearFilters = () => {
        setCategoryFilter('all');
        setSubCategoryFilter('all');
        setStatusFilter('all');
        setAvailabilityFilter('all');
        setSortBy('newest');
        setSearchStr('');
        setPage(1);
    };

    // ══════════════════════════════════════════════════════════════════════════
    // RENDER
    // ══════════════════════════════════════════════════════════════════════════
    return (
        <Box sx={{ display: 'flex', bgcolor: shopifyColors.surface, minHeight: '100vh' }}>
            {/* Sidebar */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <Slidemenu onMenuToggle={() => setMobileOpen(!mobileOpen)} />
            </Box>

            {/* Mobile Drawer */}
            <Drawer
                anchor="left"
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                ModalProps={{ keepMounted: true }}
                sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 250 } }}
            >
                <Slidemenu onMenuToggle={() => setMobileOpen(false)} mobileOpen={false} />
            </Drawer>

            {/* ── Main Content ── */}
            <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, width: '100%' }}>
                <AdminHeader onMenuToggle={() => setMobileOpen(!mobileOpen)} />

                {/* Page Header */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: shopifyColors.textPrimary, mb: 1 }}>
                        Bundle Management
                    </Typography>
                    {isVendor && (
                        <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                            Vendor: {userName} (ID: {vendorId})
                        </Typography>
                    )}

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<Refresh />}
                            onClick={() => { refetchBundles(); refetchBundleCount(); }}
                            disabled={isLoadingBundles}
                            size="small"
                            sx={{
                                textTransform: 'none',
                                borderColor: shopifyColors.border,
                                color: shopifyColors.textPrimary,
                                borderRadius: '6px',
                                fontWeight: 500,
                                width: { xs: '100%', sm: 'auto' },
                                '&:hover': { borderColor: shopifyColors.textSecondary },
                            }}
                        >
                            Refresh
                        </Button>

                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={handleAddBundle}
                            size="small"
                            sx={{
                                bgcolor: shopifyColors.primary,
                                color: 'white',
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: '6px',
                                px: 2,
                                width: { xs: '100%', sm: 'auto' },
                                '&:hover': { bgcolor: shopifyColors.primaryHover },
                            }}
                        >
                            Add New Bundle
                        </Button>
                    </Stack>
                </Box>

                {/* Search & Filter Bar */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={8}>
                        <TextField
                            placeholder="Search bundles by name or description..."
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={searchStr}
                            onChange={(e) => { setSearchStr(e.target.value); setPage(1); }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px', bgcolor: 'white' } }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search sx={{ color: shopifyColors.textSecondary }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchStr ? (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setSearchStr('')} edge="end" size="small">
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Button
                            variant="outlined"
                            startIcon={isFilterActive ? <Badge color="primary" variant="dot"><FilterAlt /></Badge> : <FilterAlt />}
                            onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                            size="small"
                            fullWidth
                            sx={{
                                textTransform: 'none',
                                borderColor: shopifyColors.border,
                                color: isFilterActive ? shopifyColors.primary : shopifyColors.textPrimary,
                                borderRadius: '6px',
                                fontWeight: 500,
                                '&:hover': { borderColor: shopifyColors.border },
                            }}
                        >
                            Filter &amp; Sort
                        </Button>
                    </Grid>
                </Grid>

                {/* Filter Popover */}
                <Popover
                    open={Boolean(filterAnchorEl)}
                    anchorEl={filterAnchorEl}
                    onClose={() => setFilterAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    PaperProps={{
                        sx: { mt: 1, p: 2, minWidth: 300, borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
                    }}
                >
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Filters &amp; Sorting
                    </Typography>

                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Category</InputLabel>
                        <Select
                            value={categoryFilter}
                            onChange={(e) => {
                                setCategoryFilter(e.target.value);
                                setSubCategoryFilter('all');
                                setPage(1);
                            }}
                            label="Category"
                        >
                            <MenuItem value="all">All Categories</MenuItem>
                            {sortedCategories.map((c: any) => (
                                <MenuItem key={c.cat_id || c.id} value={c.cat_id || c.id}>
                                    {c.cat_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {categoryFilter !== 'all' && filteredSubCategoriesForFilter.length > 0 && (
                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                            <InputLabel>Subcategory</InputLabel>
                            <Select
                                value={subCategoryFilter}
                                onChange={(e) => { setSubCategoryFilter(e.target.value); setPage(1); }}
                                label="Subcategory"
                            >
                                <MenuItem value="all">All Subcategories</MenuItem>
                                {filteredSubCategoriesForFilter.map((sc: any) => (
                                    <MenuItem key={sc.sub_cat_id || sc.id} value={sc.sub_cat_id || sc.id}>
                                        {sc.sub_cat_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Status</InputLabel>
                        <Select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} label="Status">
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="1">Active</MenuItem>
                            <MenuItem value="0">Inactive</MenuItem>
                            <MenuItem value="2">Suspended</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Availability</InputLabel>
                        <Select value={availabilityFilter} onChange={(e) => { setAvailabilityFilter(e.target.value); setPage(1); }} label="Availability">
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="1">In Stock</MenuItem>
                            <MenuItem value="0">Out of Stock</MenuItem>
                            <MenuItem value="2">Pre-order</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Sort By</InputLabel>
                        <Select value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(1); }} label="Sort By">
                            <MenuItem value="newest">Newest First</MenuItem>
                            <MenuItem value="oldest">Oldest First</MenuItem>
                            <MenuItem value="name_asc">Name (A–Z)</MenuItem>
                            <MenuItem value="name_desc">Name (Z–A)</MenuItem>
                            <MenuItem value="price_asc">Price (Low → High)</MenuItem>
                            <MenuItem value="price_desc">Price (High → Low)</MenuItem>
                            <MenuItem value="stock_asc">Stock (Low → High)</MenuItem>
                            <MenuItem value="stock_desc">Stock (High → Low)</MenuItem>
                        </Select>
                    </FormControl>

                    {isFilterActive && (
                        <Button
                            variant="outlined"
                            startIcon={<Clear />}
                            onClick={() => { handleClearFilters(); setFilterAnchorEl(null); }}
                            size="small"
                            fullWidth
                            sx={{
                                textTransform: 'none',
                                borderColor: shopifyColors.error,
                                color: shopifyColors.error,
                                mt: 2,
                                '&:hover': { bgcolor: 'rgba(215,44,13,0.04)' },
                            }}
                        >
                            Clear All Filters
                        </Button>
                    )}
                </Popover>

                {/* Result Count */}
                {!isLoadingBundles && !isBundlesError && (
                    <Typography variant="body2" sx={{ mb: 2, color: shopifyColors.textSecondary }}>
                        Showing {filteredBundles.length} of {bundleTotalRecords} bundle{bundleTotalRecords !== 1 ? 's' : ''}
                        {isFilterActive || searchStr ? ' (filtered)' : ''}
                        {isVendor && ' • Your bundles only'}
                    </Typography>
                )}

                {/* Loading */}
                {isLoadingBundles && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }}>Loading bundles...</Typography>
                    </Box>
                )}

                {/* Error */}
                {isBundlesError && !isLoadingBundles && (
                    <Alert
                        severity="error"
                        action={<Button onClick={() => refetchBundles()} size="small">Retry</Button>}
                        sx={{ mb: 3 }}
                    >
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>Failed to load bundles</Typography>
                        <Typography variant="body2">
                            {bundlesError?.toString() || 'Please check your connection and try again.'}
                        </Typography>
                    </Alert>
                )}

                {/* Empty State */}
                {!isLoadingBundles && !isBundlesError && filteredBundles.length === 0 && (
                    <Card sx={{ p: 4, textAlign: 'center', bgcolor: 'white', mb: 3 }}>
                        <Inventory sx={{ fontSize: 64, color: shopifyColors.textSecondary, mb: 2 }} />
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                            {searchStr || isFilterActive ? 'No bundles found' : 'No bundles available'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 3 }}>
                            {searchStr
                                ? `Nothing matches "${searchStr}"`
                                : isFilterActive
                                    ? 'Try adjusting your filters'
                                    : 'Add your first bundle to get started'}
                        </Typography>

                        {!searchStr && !isFilterActive && (
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={handleAddBundle}
                                sx={{ bgcolor: shopifyColors.primary, color: 'white', textTransform: 'none', fontWeight: 600, borderRadius: '6px', '&:hover': { bgcolor: shopifyColors.primaryHover } }}
                            >
                                Add First Bundle
                            </Button>
                        )}
                        {(searchStr || isFilterActive) && (
                            <Button variant="outlined" startIcon={<Clear />} onClick={handleClearFilters} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '6px' }}>
                                Clear Search &amp; Filters
                            </Button>
                        )}
                    </Card>
                )}

                {/* ── Bundle Table ── */}
                {!isLoadingBundles && filteredBundles.length > 0 && (
                    <TableContainer component={Paper} sx={{ borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}>
                        <Table>
                            <TableHead sx={{ bgcolor: shopifyColors.surface }}>
                                <TableRow>
                                    {['#', 'Image', 'Bundle Name', 'Price', 'Status', 'Actions'].map((h) => (
                                        <TableCell key={h} sx={{ fontWeight: 600, color: shopifyColors.textPrimary, fontSize: '12px', textTransform: 'uppercase' }}>
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredBundles.map((bundle, idx) => (
                                    <TableRow key={bundle.bundle_id || bundle.id || idx} sx={{ '&:hover': { bgcolor: shopifyColors.surface } }}>
                                        {/* # */}
                                        <TableCell>{(page - 1) * productsPerPage + idx + 1}</TableCell>

                                        {/* Image */}
                                        <TableCell>
                                            <Avatar
                                                src={bundle.bundle_main_image ? `https://fftestservice.sarsetech.com/images/${bundle.bundle_main_image}` : ''}
                                                alt={bundle.bundle_name}
                                                sx={{ width: 50, height: 50, borderRadius: '6px' }}
                                            >
                                                {bundle.bundle_name?.[0] || 'B'}
                                            </Avatar>
                                        </TableCell>

                                        {/* Name */}
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{bundle.bundle_name}</Typography>
                                            <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, display: 'block' }}>
                                                {bundle.bundle_short_name}
                                            </Typography>
                                            {!isVendor && bundle.vendor_name && (
                                                <Typography variant="caption" sx={{ color: shopifyColors.blue, display: 'block', mt: 0.5 }}>
                                                    Vendor: {bundle.vendor_name}
                                                </Typography>
                                            )}
                                        </TableCell>

                                        {/* Category — FIX: use cat_id (number) to look up name */}


                                        {/* Price */}
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: shopifyColors.primary }}>
                                                {formatCurrencyAED(bundle.bundle_retail_price || 0)}
                                            </Typography>
                                        </TableCell>

                                        {/* Status */}
                                        <TableCell>{renderStatus(bundle.status ?? 1)}</TableCell>

                                        {/* Actions */}
                                        <TableCell>
                                            <Stack direction="row" spacing={1}>
                                                <Tooltip title="Edit Bundle">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleEditBundle(bundle)}
                                                            disabled={isVendor && bundle.vendor_id !== vendorId}
                                                            sx={{ color: shopifyColors.textSecondary, '&:hover': { bgcolor: '#f1f2f4', color: shopifyColors.textPrimary } }}
                                                        >
                                                            <Edit fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>

                                                <Tooltip title="Delete Bundle">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDeleteClick(bundle)}
                                                            disabled={(isVendor && bundle.vendor_id !== vendorId) || isDeletingBundle}
                                                            sx={{ color: shopifyColors.textSecondary, '&:hover': { bgcolor: '#fdeded', color: shopifyColors.error } }}
                                                        >
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* Pagination */}
                {bundleTotalPages > 1 && !isLoadingBundles && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                        <Pagination
                            count={bundleTotalPages}
                            page={page}
                            onChange={(_, p) => setPage(p)}
                            sx={{
                                '& .Mui-selected': { bgcolor: shopifyColors.primary + ' !important', color: 'white' },
                                '& .MuiPaginationItem-root:hover': { bgcolor: '#f1f2f4' },
                            }}
                        />
                    </Box>
                )}

                {/* ════════════════════════════════════════════════════════════════
                BUNDLE FORM DIALOG
                ════════════════════════════════════════════════════════════════ */}
                <Dialog
                    open={formOpen}
                    onClose={handleFormClose}
                    maxWidth="md"
                    fullWidth
                    fullScreen={isMobile}
                    PaperProps={{ sx: { borderRadius: '8px' } }}
                >
                    <DialogTitle sx={{ borderBottom: `1px solid ${shopifyColors.border}`, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {isEditingBundle ? 'Edit Bundle' : 'Add New Bundle'}
                        </Typography>
                        <IconButton onClick={handleFormClose} size="small"><Close /></IconButton>
                    </DialogTitle>

                    <DialogContent dividers>
                        <Box component="form" onSubmit={handleSubmit}>
                            {/* Tabs */}
                            <Tabs
                                value={tabValue}
                                onChange={(_, v) => setTabValue(v)}
                                variant="scrollable"
                                scrollButtons="auto"
                                sx={{
                                    mb: 2,
                                    borderBottom: `1px solid ${shopifyColors.border}`,
                                    '& .MuiTab-root': { minWidth: 'auto', padding: { xs: '12px 8px', sm: '12px 16px' }, fontSize: { xs: '12px', sm: '14px' }, fontWeight: 600 },
                                }}
                            >
                                <Tab
                                    label="Basic Info"
                                    icon={<Inventory fontSize="small" />}
                                    iconPosition="start"
                                    sx={{ color: formErrors.bundle_name || formErrors.bundle_short_name || formErrors.cat_id ? 'error.main' : 'inherit' }}
                                />
                                <Tab
                                    label="Pricing"
                                    icon={<AttachMoney fontSize="small" />}
                                    iconPosition="start"
                                    sx={{ color: formErrors.bundle_price || formErrors.bundle_retail_price ? 'error.main' : 'inherit' }}
                                />
                                <Tab
                                    label="Products"
                                    icon={<ShoppingBasket fontSize="small" />}
                                    iconPosition="start"
                                    sx={{ color: formErrors.products ? 'error.main' : 'inherit' }}
                                />
                                <Tab label="Shipping" icon={<LocalShipping fontSize="small" />} iconPosition="start" />
                            </Tabs>

                            {/* Error summary */}
                            {Object.keys(formErrors).length > 0 && (
                                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormErrors({})}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Please fix the following errors:</Typography>
                                    <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                                        {Object.entries(formErrors).filter(([, msg]) => msg).map(([field, msg]) => (
                                            <li key={field}><Typography variant="caption">{msg}</Typography></li>
                                        ))}
                                    </Box>
                                </Alert>
                            )}

                            {/* ── Tab 0: Basic Info ── */}
                            <TabPanel value={tabValue} index={0}>
                                <Grid container spacing={3}>
                                    {/* FIX: Image column — fixed width, proper min-height so it doesn't crush */}
                                    <Grid item xs={12} sm={4}>
                                        <Card
                                            sx={{
                                                p: 2,
                                                textAlign: 'center',
                                                cursor: 'pointer',
                                                border: `2px dashed ${shopifyColors.border}`,
                                                minHeight: 200,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                '&:hover': { borderColor: shopifyColors.primary },
                                            }}
                                            onClick={() => document.getElementById('bundle-image-upload')?.click()}
                                        >
                                            {!imagePreview ? (
                                                <>
                                                    <PhotoCamera sx={{ fontSize: 48, color: shopifyColors.textSecondary, mb: 1 }} />
                                                    <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>Click to upload image</Typography>
                                                    <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, mt: 0.5 }}>JPG, PNG, GIF (max 5MB)</Typography>
                                                </>
                                            ) : (
                                                <Box sx={{ width: '100%' }}>
                                                    <Box
                                                        component="img"
                                                        src={imagePreview}
                                                        alt="Bundle"
                                                        sx={{ width: '100%', maxHeight: 180, objectFit: 'contain', borderRadius: '8px' }}
                                                    />
                                                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: shopifyColors.textSecondary }}>Click to change</Typography>
                                                </Box>
                                            )}
                                        </Card>

                                        <input
                                            type="file"
                                            id="bundle-image-upload"
                                            accept="image/jpeg,image/jpg,image/png,image/gif"
                                            onChange={handleImageChange}
                                            style={{ display: 'none' }}
                                        />

                                        <Button
                                            variant="outlined"
                                            startIcon={<PhotoCamera />}
                                            onClick={() => document.getElementById('bundle-image-upload')?.click()}
                                            disabled={isFormSubmitting}
                                            fullWidth
                                            size="small"
                                            sx={{ mt: 1 }}
                                        >
                                            {imagePreview ? 'Change Image' : 'Upload Image'}
                                        </Button>

                                        {imagePreview && (
                                            <Button variant="text" color="error" onClick={handleRemoveImage} disabled={isFormSubmitting} fullWidth size="small" sx={{ mt: 0.5 }}>
                                                Remove Image
                                            </Button>
                                        )}
                                    </Grid>

                                    {/* FIX: Form fields column — takes remaining space, all fields visible */}
                                    <Grid item xs={12} sm={8}>
                                        <Grid container spacing={2}>
                                            {/* Bundle Name */}
                                            <Grid item xs={12}>
                                                <TextField
                                                    required
                                                    fullWidth
                                                    name="bundle_name"
                                                    label="Bundle Name"
                                                    value={bundleFormData.bundle_name}
                                                    onChange={handleInputChange}
                                                    error={Boolean(formErrors.bundle_name)}
                                                    helperText={formErrors.bundle_name}
                                                    disabled={isFormSubmitting}
                                                    size="small"
                                                />
                                            </Grid>

                                            {/* Short Name */}
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    required
                                                    fullWidth
                                                    name="bundle_short_name"
                                                    label="Short Name"
                                                    value={bundleFormData.bundle_short_name}
                                                    onChange={handleInputChange}
                                                    error={Boolean(formErrors.bundle_short_name)}
                                                    helperText={formErrors.bundle_short_name || 'Max 10 characters'}
                                                    inputProps={{ maxLength: 10 }}
                                                    disabled={isFormSubmitting}
                                                    size="small"
                                                />
                                            </Grid>

                                            {/* Category */}
                                            <Grid item xs={12} md={6}>
                                                <FormControl fullWidth required error={Boolean(formErrors.cat_id)} size="small">
                                                    <InputLabel>Category</InputLabel>
                                                    <Select
                                                        name="cat_id"
                                                        value={bundleFormData.cat_id || ''}
                                                        onChange={handleCategoryChange}
                                                        label="Category"
                                                        disabled={isFormSubmitting}
                                                    >
                                                        <MenuItem value="">Select Category</MenuItem>
                                                        {sortedCategories.map((c: any) => (
                                                            <MenuItem key={c.cat_id || c.id} value={c.cat_id || c.id}>
                                                                {c.cat_name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    {formErrors.cat_id && <FormHelperText>{formErrors.cat_id}</FormHelperText>}
                                                </FormControl>
                                            </Grid>

                                            {/* Sub Category — FIX: properly enabled/disabled, value bound */}
                                            <Grid item xs={12} md={6}>
                                                <FormControl fullWidth size="small" disabled={!bundleFormData.cat_id || isFormSubmitting}>
                                                    <InputLabel>Sub Category</InputLabel>
                                                    <Select
                                                        name="sub_cat_id"
                                                        value={bundleFormData.sub_cat_id || ''}
                                                        onChange={handleSelectChange}
                                                        label="Sub Category"
                                                    >
                                                        <MenuItem value="">Select Sub Category</MenuItem>
                                                        {paginatedSubCategories.map((sc: any) => (
                                                            <MenuItem key={sc.sub_cat_id || sc.id} value={sc.sub_cat_id || sc.id}>
                                                                {sc.sub_cat_name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            {/* Status */}
                                            <Grid item xs={12} md={6}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Status</InputLabel>
                                                    <Select name="status" value={bundleFormData.status} onChange={handleSelectChange} label="Status" disabled={isFormSubmitting}>
                                                        <MenuItem value={1}>Active</MenuItem>
                                                        <MenuItem value={0}>Inactive</MenuItem>
                                                        <MenuItem value={2}>Suspended</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            {/* Availability */}
                                            <Grid item xs={12} md={6}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Availability</InputLabel>
                                                    <Select name="availability" value={bundleFormData.availability} onChange={handleSelectChange} label="Availability" disabled={isFormSubmitting}>
                                                        <MenuItem value={1}>In Stock</MenuItem>
                                                        <MenuItem value={0}>Out of Stock</MenuItem>
                                                        <MenuItem value={2}>Pre-order</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            {/* Description */}
                                            <Grid item xs={12}>
                                                <Typography variant="caption" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
                                                    Description (Optional)
                                                </Typography>
                                                <ReactQuill
                                                    value={bundleFormData.bundle_desc || ''}
                                                    onChange={(val) => setBundleFormData((prev) => ({ ...prev, bundle_desc: val }))}
                                                    modules={quillModules}
                                                    formats={quillFormats}
                                                    placeholder="Enter bundle description..."
                                                    readOnly={isFormSubmitting}
                                                    style={{ height: '140px', marginBottom: '40px' }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </TabPanel>

                            {/* ── Tab 1: Pricing ── */}
                            <TabPanel value={tabValue} index={1}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            required fullWidth name="bundle_price" label="Selling Price" type="number"
                                            value={bundleFormData.bundle_price}
                                            onChange={handleInputChange}
                                            error={Boolean(formErrors.bundle_price)}
                                            helperText={formErrors.bundle_price || 'Final price customer pays'}
                                            disabled={isFormSubmitting} size="small"
                                            InputProps={{ startAdornment: <InputAdornment position="start">د.إ</InputAdornment>, inputProps: { min: 0, step: 0.01 } }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            required fullWidth name="bundle_retail_price" label="Retail Price" type="number"
                                            value={bundleFormData.bundle_retail_price}
                                            onChange={handleInputChange}
                                            error={Boolean(formErrors.bundle_retail_price)}
                                            helperText={formErrors.bundle_retail_price || 'Original price before discount'}
                                            disabled={isFormSubmitting} size="small"
                                            InputProps={{ startAdornment: <InputAdornment position="start">د.إ</InputAdornment>, inputProps: { min: 0, step: 0.01 } }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Discount Type</InputLabel>
                                            <Select name="discount_type" value={bundleFormData.discount_type} onChange={handleSelectChange} label="Discount Type" disabled={isFormSubmitting}>
                                                <MenuItem value={0}>No Discount</MenuItem>
                                                <MenuItem value={1}>Percentage (%)</MenuItem>
                                                <MenuItem value={2}>Fixed Amount (AED)</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        {bundleFormData.discount_type > 0 && (
                                            <TextField
                                                fullWidth name="bundle_discount"
                                                label={bundleFormData.discount_type === 1 ? 'Discount %' : 'Discount Amount'}
                                                type="number" value={bundleFormData.bundle_discount}
                                                onChange={handleInputChange} disabled={isFormSubmitting} size="small"
                                                InputProps={{
                                                    endAdornment: <InputAdornment position="end">{bundleFormData.discount_type === 1 ? '%' : 'د.إ'}</InputAdornment>,
                                                    inputProps: { min: 0, step: bundleFormData.discount_type === 1 ? 1 : 0.01 },
                                                }}
                                            />
                                        )}
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth name="bundle_stock" label="Stock Quantity" type="number"
                                            value={bundleFormData.bundle_stock} onChange={handleInputChange}
                                            helperText="Available stock for this bundle"
                                            disabled={isFormSubmitting} size="small" inputProps={{ min: 0 }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth name="max_order_quantity" label="Max Order Quantity" type="number"
                                            value={bundleFormData.max_order_quantity} onChange={handleInputChange}
                                            helperText="Maximum quantity per order"
                                            disabled={isFormSubmitting} size="small" inputProps={{ min: 1 }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth name="popular_score" label="Popular Score" type="number"
                                            value={bundleFormData.popular_score} onChange={handleInputChange}
                                            helperText="Higher score = more prominent"
                                            disabled={isFormSubmitting} size="small" inputProps={{ min: 0 }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Currency</InputLabel>
                                            <Select name="currency" value={bundleFormData.currency} onChange={handleSelectChange} label="Currency" disabled={isFormSubmitting}>
                                                <MenuItem value="AED">AED (د.إ)</MenuItem>
                                                <MenuItem value="USD">USD ($)</MenuItem>
                                                <MenuItem value="GBP">GBP (£)</MenuItem>
                                                <MenuItem value="INR">INR (₹)</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormGroup>
                                            <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>Price Priority</FormLabel>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={(bundleFormData.price_priority & 1) === 1}
                                                        onChange={(e) => {
                                                            const cur = bundleFormData.price_priority;
                                                            setBundleFormData((p) => ({ ...p, price_priority: e.target.checked ? (cur === 2 ? 3 : 1) : cur === 3 ? 2 : 0 }));
                                                        }}
                                                        disabled={isFormSubmitting}
                                                    />
                                                }
                                                label="Size-based pricing"
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={(bundleFormData.price_priority & 2) === 2}
                                                        onChange={(e) => {
                                                            const cur = bundleFormData.price_priority;
                                                            setBundleFormData((p) => ({ ...p, price_priority: e.target.checked ? (cur === 1 ? 3 : 2) : cur === 3 ? 1 : 0 }));
                                                        }}
                                                        disabled={isFormSubmitting}
                                                    />
                                                }
                                                label="Color-based pricing"
                                            />
                                            <FormHelperText>Select which price variations are available. You may select both.</FormHelperText>
                                        </FormGroup>
                                    </Grid>
                                </Grid>
                            </TabPanel>

                            {/* ── Tab 2: Products ── */}
                            <TabPanel value={tabValue} index={2}>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Select Products for Bundle</Typography>

                                {formErrors.products && <Alert severity="error" sx={{ mb: 2 }}>{formErrors.products}</Alert>}

                                <Grid container spacing={2}>
                                    {/* Available products list */}
                                    <Grid item xs={12} md={6}>
                                        <Card sx={{ height: '100%' }}>
                                            <Box sx={{ p: 2, borderBottom: `1px solid ${shopifyColors.border}` }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                    Available Products ({availableProducts.length})
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: shopifyColors.textSecondary }}>Click to select / deselect</Typography>
                                            </Box>
                                            <Box sx={{ maxHeight: 400, overflowY: 'auto', p: 1 }}>
                                                {isLoadingProducts ? (
                                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={24} /></Box>
                                                ) : availableProducts.length === 0 ? (
                                                    <Typography variant="body2" sx={{ textAlign: 'center', p: 3, color: shopifyColors.textSecondary }}>No products available</Typography>
                                                ) : (
                                                    <List dense>
                                                        {availableProducts.map((prod: any) => {
                                                            const pid = prod.product_id || prod.id;
                                                            const chosen = selectedProducts.some((s) => s.product_id === pid);
                                                            return (
                                                                <ListItemButton key={pid} onClick={() => handleProductSelection(prod)} selected={chosen} sx={{ borderRadius: '6px', mb: 0.5 }}>
                                                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                                                        <Checkbox checked={chosen} size="small" />
                                                                    </ListItemIcon>
                                                                    <Avatar
                                                                        src={prod.product_main_image ? `https://fftestservice.sarsetech.com/images/${prod.product_main_image}` : ''}
                                                                        sx={{ width: 40, height: 40, mr: 2, borderRadius: '6px' }}
                                                                    >
                                                                        {prod.prod_name?.[0] || 'P'}
                                                                    </Avatar>
                                                                    <ListItemText
                                                                        primary={<Typography variant="body2" sx={{ fontWeight: chosen ? 600 : 400 }}>{prod.prod_name}</Typography>}
                                                                        secondary={<Typography variant="caption" sx={{ color: shopifyColors.textSecondary }}>{formatCurrencyAED(prod.product_price || 0)}</Typography>}
                                                                    />
                                                                </ListItemButton>
                                                            );
                                                        })}
                                                    </List>
                                                )}
                                            </Box>
                                        </Card>
                                    </Grid>

                                    {/* Selected products list */}
                                    <Grid item xs={12} md={6}>
                                        <Card sx={{ height: '100%' }}>
                                            <Box sx={{ p: 2, borderBottom: `1px solid ${shopifyColors.border}` }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                    Selected Products ({selectedProducts.length})
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: shopifyColors.textSecondary }}>Adjust quantities as needed</Typography>
                                            </Box>
                                            <Box sx={{ maxHeight: 400, overflowY: 'auto', p: 1 }}>
                                                {selectedProducts.length === 0 ? (
                                                    <Typography variant="body2" sx={{ textAlign: 'center', p: 3, color: shopifyColors.textSecondary }}>No products selected yet</Typography>
                                                ) : (
                                                    <List dense>
                                                        {selectedProducts.map((bp) => (
                                                            <ListItem
                                                                key={bp.product_id}
                                                                secondaryAction={
                                                                    <IconButton
                                                                        edge="end" size="small"
                                                                        onClick={() => handleProductSelection({
                                                                            product_id: bp.product_id,
                                                                            prod_name: bp.product_name,
                                                                            product_price: bp.product_price,
                                                                            product_main_image: bp.product_main_image,
                                                                        })}
                                                                    >
                                                                        <Delete fontSize="small" />
                                                                    </IconButton>
                                                                }
                                                                sx={{ mb: 1, bgcolor: '#f9f9fa', borderRadius: '6px' }}
                                                            >
                                                                <Avatar
                                                                    src={bp.product_main_image ? `https://fftestservice.sarsetech.com/images/${bp.product_main_image}` : ''}
                                                                    sx={{ width: 40, height: 40, mr: 2, borderRadius: '6px' }}
                                                                >
                                                                    {bp.product_name?.[0] || 'P'}
                                                                </Avatar>
                                                                <ListItemText
                                                                    primary={<Typography variant="body2" sx={{ fontWeight: 600 }}>{bp.product_name}</Typography>}
                                                                    secondary={
                                                                        <>
                                                                            <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, display: 'block' }}>
                                                                                Price: {formatCurrencyAED(bp.product_price || 0)}
                                                                            </Typography>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                                                                <Typography variant="caption" sx={{ mr: 1 }}>Qty:</Typography>
                                                                                <TextField
                                                                                    size="small" type="number"
                                                                                    value={productQuantities[bp.product_id] || bp.quantity || 1}
                                                                                    onChange={(e) => handleProductQuantityChange(bp.product_id, Math.max(1, parseInt(e.target.value) || 1))}
                                                                                    inputProps={{ min: 1 }}
                                                                                    sx={{ width: 70 }}
                                                                                />
                                                                            </Box>
                                                                        </>
                                                                    }
                                                                />
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                )}
                                            </Box>
                                        </Card>
                                    </Grid>

                                    {/* Bundle Summary */}
                                    {selectedProducts.length > 0 && (
                                        <Grid item xs={12}>
                                            <Card sx={{ p: 2, bgcolor: '#f9f9fa' }}>
                                                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Bundle Summary</Typography>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={6} md={3}>
                                                        <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, display: 'block' }}>Total Products</Typography>
                                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedProducts.length}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6} md={3}>
                                                        <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, display: 'block' }}>Total Items</Typography>
                                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                            {selectedProducts.reduce((s, p) => s + (productQuantities[p.product_id] || 1), 0)}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={6} md={3}>
                                                        <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, display: 'block' }}>Individual Total</Typography>
                                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                            {formatCurrencyAED(selectedProducts.reduce((s, p) => s + (p.product_price || 0) * (productQuantities[p.product_id] || 1), 0))}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={6} md={3}>
                                                        <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, display: 'block' }}>Bundle Price</Typography>
                                                        <Typography variant="body1" sx={{ fontWeight: 600, color: shopifyColors.primary }}>
                                                            {formatCurrencyAED(bundleFormData.bundle_price)}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </Card>
                                        </Grid>
                                    )}
                                </Grid>
                            </TabPanel>

                            {/* ── Tab 3: Shipping ── */}
                            <TabPanel value={tabValue} index={3}>
                                <Grid container spacing={3}>
                                    {/* Shipping Model */}
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Shipping Configuration</Typography>

                                        {shippingModels.length > 0 ? (
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Shipping Model</InputLabel>
                                                <Select
                                                    name="shipping_model"
                                                    value={bundleFormData.shipping_model || ''}
                                                    onChange={handleSelectChange}
                                                    label="Shipping Model"
                                                    disabled={isFormSubmitting}
                                                >
                                                    {shippingModels.map((m) => (
                                                        <MenuItem key={m.id} value={m.id}>{m.model_name}</MenuItem>
                                                    ))}
                                                </Select>
                                                <FormHelperText>Select shipping method for this bundle</FormHelperText>
                                            </FormControl>
                                        ) : (
                                            <Alert severity="info"><Typography variant="body2">No shipping models available</Typography></Alert>
                                        )}
                                    </Grid>

                                    {/* Delivery Options — FIX: reads directly from bundleFormData.delivery_model */}
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Delivery Options</Typography>

                                        {availableDeliveryModels.length === 0 ? (
                                            <Alert severity="info"><Typography variant="body2">No delivery models available</Typography></Alert>
                                        ) : (
                                            <>
                                                <FormGroup>
                                                    {availableDeliveryModels.map((model) => {
                                                        const currentModels = getCurrentDeliveryModels();
                                                        const isSelected = currentModels.includes(model.id);

                                                        return (
                                                            <FormControlLabel
                                                                key={model.id}
                                                                control={
                                                                    <Checkbox
                                                                        checked={isSelected}
                                                                        onChange={(e) => {
                                                                            const cur = getCurrentDeliveryModels();
                                                                            const next = e.target.checked
                                                                                ? [...cur, model.id]
                                                                                : cur.filter((id) => id !== model.id);
                                                                            setDeliveryModels(next);
                                                                        }}
                                                                        disabled={isFormSubmitting}
                                                                    />
                                                                }
                                                                label={
                                                                    <Box>
                                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                            {model.model_name}
                                                                            {model.is_default === 1 && <Chip label="Default" size="small" sx={{ ml: 1 }} />}
                                                                        </Typography>
                                                                        <Typography variant="caption" sx={{ color: shopifyColors.textSecondary }}>
                                                                            {model.delivery_type === 0 ? `${model.hours}h delivery` : `${model.days}d delivery`}
                                                                        </Typography>
                                                                    </Box>
                                                                }
                                                            />
                                                        );
                                                    })}
                                                </FormGroup>
                                                <FormHelperText sx={{ mt: 1 }}>
                                                    {getCurrentDeliveryModels().length} method(s) selected
                                                </FormHelperText>
                                            </>
                                        )}
                                    </Grid>
                                </Grid>
                            </TabPanel>
                        </Box>
                    </DialogContent>

                    {/* Dialog Actions */}
                    <DialogActions sx={{ borderTop: `1px solid ${shopifyColors.border}`, p: 2, gap: 1 }}>
                        <Button onClick={handleFormClose} disabled={isFormSubmitting} sx={{ textTransform: 'none' }}>Cancel</Button>
                        <Button
                            type="submit"
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={isFormSubmitting || isAddingBundle || isUpdatingBundle}
                            sx={{ bgcolor: shopifyColors.primary, color: 'white', textTransform: 'none', '&:hover': { bgcolor: shopifyColors.primaryHover } }}
                            startIcon={(isFormSubmitting || isAddingBundle || isUpdatingBundle) ? <CircularProgress size={20} color="inherit" /> : null}
                        >
                            {isFormSubmitting || isAddingBundle || isUpdatingBundle
                                ? isEditingBundle ? 'Updating...' : 'Adding...'
                                : isEditingBundle ? 'Update Bundle' : 'Add Bundle'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* ── Delete Confirmation Dialog ── */}
                <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
                    <DialogTitle sx={{ borderBottom: `1px solid ${shopifyColors.border}`, pb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>Confirm Delete</Typography>
                    </DialogTitle>
                    <DialogContent sx={{ pt: 2 }}>
                        {bundleToDelete && (
                            <>
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>This action cannot be undone</Typography>
                                </Alert>
                                <Typography variant="body2" sx={{ mb: 2 }}>Are you sure you want to delete the bundle:</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600, color: shopifyColors.textPrimary, mb: 1 }}>
                                    "{bundleToDelete.bundle_name}"
                                </Typography>
                                <Typography variant="caption" sx={{ color: shopifyColors.textSecondary }}>
                                    Bundle ID: {bundleToDelete.bundle_id || bundleToDelete.id}
                                </Typography>
                            </>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ borderTop: `1px solid ${shopifyColors.border}`, p: 2, gap: 1 }}>
                        <Button onClick={handleDeleteCancel} disabled={isDeletingBundle} sx={{ textTransform: 'none' }}>Cancel</Button>
                        <Button
                            onClick={handleDeleteConfirm}
                            variant="contained"
                            color="error"
                            disabled={isDeletingBundle}
                            sx={{ textTransform: 'none' }}
                            startIcon={isDeletingBundle ? <CircularProgress size={20} color="inherit" /> : <Delete />}
                        >
                            {isDeletingBundle ? 'Deleting...' : 'Delete Bundle'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* ── Global Snackbar ── */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                        severity={snackbar.severity}
                        variant="filled"
                        sx={{ width: '100%', borderRadius: '6px', fontWeight: 500 }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </Box>
    );
};

export default BundleManagement;